import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal, Input, TextArea, useToastActions } from '@airclou/ui';
import { updateGroup } from '@/api/groups';
import type { CompanyGroup } from '@/api/groups';

interface EditGroupModalProps {
  companyId: string;
  group: CompanyGroup;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditGroupModal({ companyId, group, isOpen, onClose, onSuccess }: EditGroupModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error: showError } = useToastActions();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description || '');

  const handleSubmit = async () => {
    if (!name.trim()) return;

    setLoading(true);
    try {
      await updateGroup(companyId, group.id, {
        name: name.trim(),
        description: description.trim() || undefined,
      });
      success(t('groups.groupCreated'));
      onSuccess();
    } catch (err: any) {
      const code = err.code;
      if (code === 'GROUP_NAME_EXISTS') {
        showError(t('groups.groupNameExists'));
      } else if (code === 'INVALID_NAME') {
        showError(t('groups.groupNameRequired'));
      } else {
        showError(err.message || t('groups.failedToCreate'));
      }
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = name.trim().length > 0 && name.trim().length <= 100 && !loading;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('groups.editGroup')}
      size="md"
      submitText={loading ? t('common.saving') : t('common.save')}
      canSubmit={canSubmit}
      isLoading={loading}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <div>
          <label htmlFor="edit-group-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('groups.groupName')} <span className="text-red-500">*</span>
          </label>
          <Input
            id="edit-group-name"
            type="text"
            value={name}
            onChange={(value) => setName(value)}
            placeholder="e.g., Engineering"
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('groups.maxCharacters')}
          </p>
        </div>

        <div>
          <label htmlFor="edit-group-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('common.description')}
          </label>
          <TextArea
            id="edit-group-description"
            value={description}
            onChange={setDescription}
            placeholder={t('groups.productDevTeam')}
            minRows={3}
            maxRows={6}
            resize="none"
          />
        </div>
      </div>
    </FormModal>
  );
}
